import { ArrowLeft, ArrowRight, Button, Delete, Heading, TextArea } from '@core/ui';
import { t } from 'i18next';
import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { ChatTimelineEvent, Timeline } from '../../../parser';
import { TimestampInput } from '../../molecules';

export const ChatEditorOrganism = ({ timeline, currentTime, onSeek }: { timeline: Timeline; currentTime: Duration; onSeek: (seek: Duration) => void }) => {
    const [previousChat, setPreviousChat] = useState<ChatTimelineEvent>();
    const [nextChat, setNextChat] = useState<ChatTimelineEvent>();
    const [currentChat, setCurrentChat] = useState<ChatTimelineEvent>();
    const [currentChatIndex, setCurrentChatIndex] = useState<number>(-1);
    const [currentMessage, setCurrentMessage] = useState<string>('');

    useEffect(() => {
        if (!timeline?.chat) {
            setPreviousChat(undefined);
            setCurrentChat(undefined);
            setNextChat(undefined);
            return;
        }

        let prevChat: ChatTimelineEvent | undefined = undefined;
        let currChat: ChatTimelineEvent | undefined = undefined;
        let currIndex: number = -1;
        let nextChat: ChatTimelineEvent | undefined = undefined;
        for (let i = 0; i < timeline.chat.length; i++) {
            if (timeline.chat[i].timestamp <= currentTime) {
                if (i > 0) {
                    prevChat = timeline.chat[i - 1];
                }

                currChat = timeline.chat[i];
                currIndex = i;

                if (i < timeline.chat.length - 1) {
                    nextChat = timeline.chat[i + 1];
                }
            }
        }

        if (!currChat && timeline.chat.length > 0) {
            nextChat = timeline.chat[0];
        }

        setPreviousChat(prevChat);
        setCurrentChat(currChat);
        setCurrentChatIndex(currIndex);
        setCurrentMessage(currChat?.message.message || '');
        setNextChat(nextChat);
    }, [timeline?.chat, currentTime]);

    useEffect(() => {
        if (!currentChat) {
            return;
        }

        const timeout = setTimeout(() => {
            currentChat.changed = currentChat.changed || currentChat.message.message !== currentMessage;
            currentChat.message.message = currentMessage;

            timeline.changed = timeline.changed || currentChat.changed;
        }, 250);
        return () => clearTimeout(timeout);
    }, [currentMessage, currentChat]);

    const onDelete = () => {
        if (currentChatIndex === -1) {
            return;
        }

        timeline.chat.splice(currentChatIndex, 1);
        timeline.chat = [...timeline.chat];
    };

    return (
        <div className="bg-gray-100 border border-gray-500 flex flex-col gap-4 pb-2">
            {currentChatIndex !== -1 && currentChat && (
                <>
                    <div className="flex-1 flex flex-col bg-white rounded-b-webinarsub p-4">
                        <div className="flex items-center justify-between gap-2 mb-4">
                            <Heading variant="h2">{t('Editor.Chat.Heading')}</Heading>
                            <Button variant="warning-outline" onClick={onDelete} iconbtn classNames="ms-auto">
                                <Delete classNames="size-6 flex-none fill-current" />
                                <span className="sr-only">{t('Editor.Chat.Button.Delete')}</span>
                            </Button>
                        </div>
                        <div className="flex-1 flex items-center justify-center gap-x-2">
                            <Button
                                variant="primary"
                                iconbtn
                                onClick={() => onSeek(previousChat?.timestamp || Duration.fromMillis(0))}
                                disabled={!previousChat}
                            >
                                <span className="sr-only">{t('Chapter.Button.Previous')}</span>
                                <ArrowLeft classNames="size-6 fill-current" />
                            </Button>
                            <div className="relative w-full min-w-0 max-w-md inline-block rounded-b-lg text-sm wrap-break-word pt-3 pb-4 px-4 bg-chat-own-msg rounded-tl-lg ml-2 mr-4 before:-right-3 before:-scale-x-100 chat-tail before:absolute before:top-0 before:w-3.5 before:h-3.5 before:bg-inherit">
                                <TextArea
                                    label={t('Editor.Chat.Message.Label')}
                                    value={currentMessage}
                                    onChange={(e) => setCurrentMessage(e.target.value)}
                                    classNames="mb-0"
                                />
                            </div>
                            <Button variant="primary" iconbtn onClick={() => onSeek(nextChat?.timestamp || Duration.fromMillis(0))} disabled={!nextChat}>
                                <span className="sr-only">{t('Chapter.Button.Next')}</span>
                                <ArrowRight classNames="size-6 fill-current" />
                            </Button>
                        </div>
                    </div>
                    <TimestampInput timestamp={currentChat.timestamp} onChange={(timestamp) => (currentChat.timestamp = timestamp)} />
                </>
            )}
        </div>
    );
};
